import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronRight, Check } from "lucide-react";
import { logger } from "@/lib/logger";

const STEPS = [
  {
    key: "creating",
    q: "What are you creating?",
    opts: ["Feature film", "Short film", "TV series", "Web series", "Commercial", "Music video", "Other"],
  },
  {
    key: "role",
    q: "What is your role?",
    opts: ["Writer", "Director", "Producer", "Writer / Director", "Creative team"],
  },
  {
    key: "experience",
    q: "How experienced are you?",
    opts: ["Beginner", "Intermediate", "Professional"],
  },
  {
    key: "goal",
    q: "What do you want to accomplish?",
    opts: ["Develop an idea", "Write a screenplay", "Rewrite a screenplay", "Prepare for production", "Create visuals", "Collaborate"],
  },
];

const ROLE_MAP = {
  "Writer": "writer",
  "Director": "director",
  "Producer": "producer",
  "Writer / Director": "writer",
  "Creative team": "collaborator",
};

export default function Onboarding() {
  const [i, setI] = React.useState(0);
  const [ans, setAns] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const step = STEPS[i];

  const choose = (opt) => setAns((a) => ({ ...a, [step.key]: opt }));

  const next = async () => {
    if (!ans[step.key]) return;
    if (i < STEPS.length - 1) {
      setI(i + 1);
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/onboard", {
        creating: ans.creating,
        role: ROLE_MAP[ans.role] || "writer",
        experience: ans.experience,
        goal: ans.goal,
      });
      // Seed demo project
      try {
        await api.post("/seed-demo");
      } catch (err) {
        logger.warn("onboarding: demo seed skipped", err);
      }
      await refresh();
      toast.success("Workspace ready. Welcome.");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error("Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFF0] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, idx) => (
            <div key={s.key}
              className={`h-0.5 flex-1 ${idx <= i ? "bg-[#D4AF37]" : "bg-[#27272A]"}`}
              data-testid={`onboarding-step-${idx}`}></div>
          ))}
        </div>
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-4">Step {i + 1} of {STEPS.length}</div>
        <h1 className="font-editorial text-4xl sm:text-5xl leading-tight mb-10" data-testid="onboarding-question">{step.q}</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {step.opts.map((opt) => {
            const active = ans[step.key] === opt;
            return (
              <button key={opt} onClick={() => choose(opt)}
                className={`text-left px-5 py-4 border transition-colors ${active ? "border-[#D4AF37] bg-[#141414]" : "border-[#27272A] hover:border-[#A1A1AA]"}`}
                data-testid={`onboarding-option-${opt.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{opt}</span>
                  {active && <Check size={16} className="text-[#D4AF37]" />}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={() => setI(Math.max(0, i - 1))}
            disabled={i === 0}
            className="text-sm text-[#71717A] hover:text-[#FFFFF0] disabled:opacity-30"
            data-testid="onboarding-back">Back</button>
          <button
            onClick={next}
            disabled={!ans[step.key] || busy}
            className="bg-[#D4AF37] text-[#0A0A0A] px-7 py-3 text-sm font-medium hover:bg-[#F59E0B] disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
            data-testid="onboarding-next">
            {i === STEPS.length - 1 ? (busy ? "Finishing…" : "Enter workspace") : "Continue"}
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
